import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Palmtree, Clock, CalendarDays, ArrowRight, Bell, ClipboardList, FileText, IdCard, CheckCircle2, Wallet, AlertTriangle, TrendingUp, TrendingDown, Minus, Send, Plus, X } from "lucide-react";
import { MONTHS_IT, fmtHours, fmtHoursSigned, fmtNum } from "@/lib/constants";
import { SuiteCard, SuiteEyebrow, SuiteButton, SuiteBadge } from "@/components/suite";
import { formatDateTimeIT } from "@/foundation/formatting";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RateiDetailDialog } from "@/components/RateiDetail";
import { formatCounterHours } from "@/lib/counterHours";

/** Formatter locale — usa fmtNum globale ma aggiunge unit. */
function fmtVal(v, unit = "h", counter = false) {
  return `${counter ? formatCounterHours(v) : fmtNum(v)} ${unit === "gg" ? "gg" : "h"}`;
}

/**
 * PoolBar — mini card contatore con barra di consumo e badge Riportato.
 * Al 100% consumato la barra è rossa, sopra l'80% è ambra, altrimenti verde.
 */
function PoolBar({ pool, label, icon: Icon, unit, onOpen, counter = false }) {
  if (!pool) {
    return (
      <div className="border border-suite-neutral-200 bg-suite-neutral-50 rounded-md p-4 opacity-60">
        <div className="text-[11px] text-suite-neutral-500 uppercase tracking-widest">{label}</div>
        <div className="text-sm text-suite-neutral-400 mt-2">Nessun dato</div>
      </div>
    );
  }
  const total = Number(pool.previous_residual || 0) + Number(pool.accrued_to_month || 0);
  const consumed = Number(pool.consumed_ytd || 0);
  const pct = total > 0 ? Math.min(100, (consumed / total) * 100) : 0;

  // Traffico luce basato su percentuale consumata.
  const tone = pct >= 100 ? "err" : pct >= 80 ? "warn" : "ok";
  const barColor =
    tone === "err" ? "bg-suite-error"
    : tone === "warn" ? "bg-suite-warning"
    : "bg-suite-accent";
  const trackColor = "bg-suite-neutral-200";

  return (
    <button type="button" onClick={onOpen}
      className="w-full text-left border border-suite-neutral-200 bg-suite-surface rounded-md p-4 hover:shadow-md hover:border-suite-primary/35 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-suite-primary/30"
      data-testid={`pool-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      aria-label={`Apri dettaglio contatore ${label}`}>
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && (
            <div className="w-9 h-9 rounded-md bg-suite-primary-light text-suite-primary flex items-center justify-center flex-shrink-0">
              <Icon size={16} strokeWidth={1.75} />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-suite-neutral-600 leading-tight">{label}</div>
            {pool.rolled_over && (
              <div className="text-[9px] text-suite-primary mt-0.5 flex items-center gap-1" title={`Riportato dal ${pool.base_year}`}>
                ⟲ Riportato dal {pool.base_year}
              </div>
            )}
          </div>
        </div>
        {tone !== "ok" && (
          <SuiteBadge tone={tone === "err" ? "err" : "warn"} size="sm">
            {pct >= 100 ? "Esaurito" : "In esaurimento"}
          </SuiteBadge>
        )}
      </div>
      <div className="text-2xl font-mono font-bold text-suite-neutral-800 mt-1">
        {fmtVal(pool.residual, unit, counter)}
        <span className="text-xs text-suite-neutral-400 font-normal ml-1">residui</span>
      </div>
      {/* Barra consumo */}
      <div className={`h-1.5 rounded-full ${trackColor} mt-3 overflow-hidden`} title={`${pct.toFixed(1)}% consumato`}>
        <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3 text-[11px]">
        <div>
          <div className="text-suite-neutral-500">Consumati</div>
          <div className="font-mono font-semibold text-suite-neutral-800">{fmtVal(consumed, unit, counter)}</div>
        </div>
        <div className="text-right">
          <div className="text-suite-neutral-500">Disponibili al mese</div>
          <div className="font-mono font-semibold text-suite-neutral-800">{fmtVal(total, unit, counter)}</div>
        </div>
      </div>
      <div className="mt-2 text-[10px] font-semibold text-suite-primary">Apri dettaglio</div>
    </button>
  );
}

/** Card grande per il saldo banca ore progressivo. */
function BankaHoursCard({ balance, monthLabel }) {
  const val = Number(balance || 0);
  const positive = val >= 0;
  const flatZero = Math.abs(val) < 0.01;
  const TrendIcon = flatZero ? Minus : positive ? TrendingUp : TrendingDown;
  return (
    <div className={`border rounded-md p-5 ${positive ? "border-suite-accent-border bg-suite-accent-light/40" : "border-suite-error-border bg-suite-error-light/40"}`}
         data-testid="employee-banca-ore-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-suite-neutral-600 flex items-center gap-1.5">
            <Wallet size={12} /> Il mio saldo banca ore
          </div>
          <div className="text-[10px] text-suite-neutral-500 mt-0.5">progressivo a {monthLabel}</div>
        </div>
        <div className={`w-11 h-11 rounded-md flex items-center justify-center ${positive ? "bg-suite-accent text-white" : "bg-suite-error text-white"}`}>
          <TrendIcon size={22} strokeWidth={2} />
        </div>
      </div>
      <div className={`text-4xl font-mono font-bold mt-3 tabular-nums ${positive ? "text-suite-accent-hover" : "text-suite-error"}`}
           data-testid="employee-banca-ore-value">
        {fmtHoursSigned(val)}
      </div>
      <div className="text-xs text-suite-neutral-500 mt-1">
        {positive
          ? "Hai un credito di ore lavorate in eccesso."
          : "Ore da recuperare o compensare."}
      </div>
    </div>
  );
}

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [semester, setSemester] = useState(null);
  const [ratei, setRatei] = useState(null);
  const [selectedRateo, setSelectedRateo] = useState(null);
  const [bankHoursOn, setBankHoursOn] = useState(true); // v0.99.30-c2
  const [notifications, setNotifications] = useState([]);
  const [monthReport, setMonthReport] = useState(null);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [now] = useState(() => new Date());
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  const loadLeaveRequests = async () => {
    try {
      const { data } = await api.get("/leave-requests/mine");
      setLeaveRequests(Array.isArray(data) ? data : []);
    } catch (_) { setLeaveRequests([]); }
  };

  // C3-B.2.8: carica la ragione sociale autoritativa dal collegamento
  // (GET /employees è auto-scoped lato backend a employee_user_links).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/employees", { params: { status: "all" } });
        const rec = Array.isArray(data) && data.length > 0 ? data[0] : null;
        if (rec?.company_name) setCompanyName(rec.company_name);
      } catch (_) { /* ignore */ }
    })();
    // C3-B.2.8: se apriamo il cruscotto con ?open_leave=1 (link da
    // EmployeeLeaves), apri direttamente il dialog di nuova richiesta.
    try {
      const qs = new URLSearchParams(window.location.search);
      if (qs.get("open_leave") === "1") setLeaveDialogOpen(true);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user?.employee_id) { setLoading(false); return; }
      setLoading(true);
      try {
        // v0.99.30-c2 · Se l'azienda del dipendente non gestisce la banca ore,
        // l'endpoint /attendance/semester restituisce 409 bank_hours_disabled.
        // Lo intercettiamo per NON mostrare card banca ore e per NON generare
        // il toast d'errore.
        const [sem, rateiResponse, notif, rep, lr] = await Promise.all([
          api.get("/attendance/semester", { params: {
            employee_id: user.employee_id,
            start_year: year,
            start_month: month <= 6 ? 1 : 7,
            length: 6,
          }}).catch((e) => {
            const code = e?.response?.data?.detail?.code;
            if (e?.response?.status === 409 &&
                ["bank_hours_disabled", "bank_hours_disabled_employee"].includes(code)) {
              return { data: null, __bank_off: true };
            }
            throw e;
          }),
          api.get("/attendance/ratei-summary", { params: {
            employee_id: user.employee_id, year, month,
          }}),
          api.get("/notifications", { params: { limit: 10 } }).catch(() => ({ data: { items: [] } })),
          api.get("/attendance/month", { params: {
            employee_id: user.employee_id, year, month,
          }}).catch(() => ({ data: null })),
          api.get("/leave-requests/mine").catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        if (sem.__bank_off) {
          setBankHoursOn(false);
          setSemester(null);
        } else {
          setBankHoursOn(true);
          setSemester(sem.data);
        }
        setRatei(rateiResponse.data);
        setNotifications(notif.data?.items || []);
        setMonthReport(rep.data);
        setLeaveRequests(Array.isArray(lr.data) ? lr.data : []);
      } catch (e) {
        toast.error(apiErr(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user, year, month]);

  const balance = semester?.final_balance ?? 0;
  const unreadCount = notifications.filter((n) => !(n.read_by || {})[user?.id]).length;
  // C3-B.2.8: richieste in attesa (in_attesa/pending/created)
  const pendingRequestsCount = leaveRequests.filter((r) =>
    ["pending", "in_attesa", "requested", "created"].includes((r.status || "").toLowerCase())
  ).length;

  return (
    <div className="space-y-6 max-w-[1400px] 3xl:max-w-[1720px]">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="employee-dashboard-root">
        <SuiteEyebrow tone="primary" icon={ClipboardList}>Il mio cruscotto</SuiteEyebrow>
        <h1 className="text-3xl font-bold tracking-tight mt-1 text-suite-neutral-800" style={{ fontFamily: "Manrope" }}>
          Ciao, {user?.name?.split(" ")[0] || "collega"} 👋
        </h1>
        {/* C3-B.2.8: ragione sociale visibile immediatamente (portale dipendente).
            Ricavata esclusivamente dal collegamento autorizzato via /employees. */}
        {companyName ? (
          <p className="text-sm mt-1 text-suite-neutral-600" data-testid="employee-dashboard-company">
            Azienda di appartenenza: <span className="font-semibold">{companyName}</span>
          </p>
        ) : (
          <p className="text-sm mt-1 text-suite-error" data-testid="employee-dashboard-company-missing">
            Nessuna azienda collegata al tuo profilo. Contatta l&apos;amministratore.
          </p>
        )}
        <p className="text-sm text-suite-neutral-500 mt-1">
          Contatori annui, saldo banca ore e notifiche — tutto in un colpo d&apos;occhio.
        </p>
      </motion.div>

      <SuiteCard
        eyebrow="Le mie richieste"
        eyebrowIcon={Palmtree}
        title={`Ferie / permessi · ${pendingRequestsCount} in attesa · ${leaveRequests.length} totali`}
        padding="md"
        testid="leave-requests-card"
        actions={
          <div className="flex gap-2">
            <SuiteButton icon={FileText} size="sm" variant="secondary" onClick={() => navigate("/assenze-permessi")}>
              Vedi tutte
            </SuiteButton>
            <SuiteButton icon={Plus} size="sm" variant="primary"
                         onClick={() => setLeaveDialogOpen(true)}
                         data-testid="new-leave-request-btn">
              Nuova richiesta
            </SuiteButton>
          </div>
        }
      >
        {leaveRequests.length === 0 ? (
          <div className="text-sm text-suite-neutral-500">Nessuna richiesta in archivio.</div>
        ) : (
          <ul className="divide-y divide-suite-neutral-100">
            {leaveRequests.slice(0, 3).map((lr) => (
              <LeaveRequestRow key={lr.id} lr={lr} onRefresh={loadLeaveRequests} />
            ))}
          </ul>
        )}
      </SuiteCard>

      {loading ? (
        <div className="text-center py-12 text-suite-neutral-400">Caricamento…</div>
      ) : (
        <>
          {/* Prima riga: 3 contatori + saldo banca ore */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="employee-pools-row">
            <PoolBar pool={ratei?.ferie_pool} label="Ferie" icon={Palmtree} unit={ratei?.ferie_pool?.unit === "hours" ? "h" : "gg"}
                     onOpen={() => setSelectedRateo({ label: "Ferie", pool: ratei?.ferie_pool })} />
            <PoolBar pool={ratei?.rol_pool} label="ROL / Permessi" icon={Clock} unit="h" counter
                     onOpen={() => setSelectedRateo({ label: "ROL / Permessi", pool: ratei?.rol_pool })} />
            <PoolBar pool={ratei?.ex_fest_pool} label="Ex Festività" icon={CalendarDays} unit="h" counter
                     onOpen={() => setSelectedRateo({ label: "Ex festività", pool: ratei?.ex_fest_pool })} />
            {bankHoursOn && (
              <BankaHoursCard balance={balance} monthLabel={`${MONTHS_IT[month - 1]} ${year}`} />
            )}
          </div>

          {/* Seconda riga: Notifiche + Azioni rapide */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <SuiteCard
              eyebrow={`Notifiche${unreadCount > 0 ? ` · ${unreadCount} non lette` : ""}`}
              eyebrowIcon={Bell}
              title="Cosa devi sapere"
              padding="none"
              testid="employee-notifications-card"
              className="lg:col-span-2 min-h-[280px]"
            >
              {notifications.length === 0 ? (
                <div className="p-10 text-center">
                  <CheckCircle2 size={28} className="mx-auto text-suite-accent mb-2" />
                  <div className="text-sm text-suite-neutral-500">Sei in pari — nessuna notifica</div>
                </div>
              ) : (
                <ul className="divide-y divide-suite-neutral-100 max-h-[420px] overflow-y-auto">
                  {notifications.map((n) => {
                    const read = !!(n.read_by || {})[user?.id];
                    const isRateiAlert = n.kind && n.kind.startsWith("ratei_") && n.kind.endsWith("_alert");
                    const p = n.payload || {};
                    return (
                      <li key={n.id} className={`p-3 flex items-start gap-3 hover:bg-suite-surface-hover ${read ? "opacity-60" : ""}`}
                          data-testid={`notif-${n.id}`}>
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isRateiAlert ? "bg-suite-warning-light text-suite-warning" : "bg-suite-primary-light text-suite-primary"}`}>
                          {isRateiAlert ? <AlertTriangle size={15} /> : <Bell size={15} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-suite-neutral-800 truncate">
                            {n.kind === "ratei_ferie_alert" && `Ferie in esaurimento (${p.percentage?.toFixed?.(0) || "?"}%)`}
                            {n.kind === "ratei_rol_alert" && `ROL / Permessi in esaurimento (${p.percentage?.toFixed?.(0) || "?"}%)`}
                            {n.kind === "ratei_ex_fest_alert" && `Ex festività in esaurimento (${p.percentage?.toFixed?.(0) || "?"}%)`}
                            {!isRateiAlert && (n.kind || "Notifica")}
                          </div>
                          {isRateiAlert && (
                            <div className="text-xs text-suite-neutral-600 mt-0.5">
                              Consumato {(p.unit === "gg" ? fmtNum : fmtHours)(p.consumed || 0)} {p.unit || "h"}
                              {" "}su {(p.unit === "gg" ? fmtNum : fmtHours)(p.total || 0)} {p.unit || "h"} disponibili nell&apos;anno {p.year || ""}
                            </div>
                          )}
                          <div className="text-[10px] text-suite-neutral-400 mt-0.5">
                            {formatDateTimeIT(n.created_at, "—")}
                          </div>
                        </div>
                        {!read && <SuiteBadge tone="warn" size="sm">Nuovo</SuiteBadge>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </SuiteCard>

            <SuiteCard
              eyebrow="Azioni rapide"
              eyebrowIcon={ArrowRight}
              title="Cosa vuoi fare?"
              padding="md"
              testid="employee-quick-actions"
              className="min-h-[280px]"
            >
              <div className="flex flex-col gap-2">
                <SuiteButton icon={ClipboardList} onClick={() => navigate("/presenze")} variant="primary" data-testid="qa-presenze">
                  Le mie presenze
                </SuiteButton>
                <SuiteButton icon={FileText} onClick={() => navigate("/report")} variant="secondary" data-testid="qa-report">
                  Il mio report
                </SuiteButton>
                <SuiteButton icon={IdCard} onClick={() => navigate("/mio-profilo")} variant="secondary" data-testid="qa-profilo">
                  Il mio profilo
                </SuiteButton>
              </div>

              {/* Riepilogo mese corrente se disponibile */}
              {monthReport && (
                <div className="mt-4 pt-3 border-t border-suite-neutral-200 text-xs">
                  <div className="text-[10px] uppercase tracking-widest text-suite-neutral-500 font-semibold mb-2">
                    Mese in corso · {MONTHS_IT[month - 1]}
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="text-suite-neutral-600">Ore teoriche</span>
                    <span className="font-mono font-semibold">{fmtHours(monthReport.totals?.theoretical || 0)} h</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="text-suite-neutral-600">Ore lavorate</span>
                    <span className="font-mono font-semibold">{fmtHours(monthReport.totals?.worked || 0)} h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-suite-neutral-600">Saldo mese</span>
                    <span className={`font-mono font-semibold ${(monthReport.balance || 0) >= 0 ? "text-suite-accent-hover" : "text-suite-error"}`}>
                      {fmtHoursSigned(monthReport.balance || 0)}
                    </span>
                  </div>
                </div>
              )}
            </SuiteCard>
          </div>

          {/* Promemoria conversione centesimi */}
          <div className="text-xs text-suite-neutral-500 italic text-center py-2">
            💡 Ricorda: le ore vanno inserite in <strong className="text-suite-primary">centesimi</strong> (es. 8h 30′ = 8,50 · minuti ÷ 60 = decimale)
          </div>

          {/* Dialog: nuova richiesta ferie/permesso */}
          <LeaveRequestDialog
            open={leaveDialogOpen}
            onOpenChange={setLeaveDialogOpen}
            pools={{
              FERIE:        { pool: ratei?.ferie_pool,   unit: ratei?.ferie_pool?.unit === "hours" ? "h" : "gg" },
              PERMESSI:     { pool: ratei?.rol_pool,     unit: "h" },
              EX_FESTIVITA: { pool: ratei?.ex_fest_pool, unit: "h" },
            }}
            onCreated={loadLeaveRequests}
          />
          <RateiDetailDialog
            open={!!selectedRateo}
            onOpenChange={(value) => !value && setSelectedRateo(null)}
            pool={selectedRateo?.pool}
            label={selectedRateo?.label}
          />
        </>
      )}
    </div>
  );
}

/** Row di una richiesta in "Le mie richieste". Con actions per cancel se pending. */
function LeaveRequestRow({ lr, onRefresh }) {
  const kindLabel = {
    FERIE: "Ferie", PERMESSI: "Permessi (ROL)", EX_FESTIVITA: "Ex festività", PERMESSI_STUDIO: "Permessi studio",
  }[lr.kind] || lr.kind;
  const statusTone = {
    pending: "warn", approved: "ok", rejected: "err", cancelled: "neutral",
  }[lr.status] || "neutral";
  const statusLabel = {
    pending: "In attesa", approved: "Approvata", rejected: "Respinta", cancelled: "Annullata",
  }[lr.status] || lr.status;
  const cancelIt = async () => {
    if (!confirm("Annullare questa richiesta?")) return;
    try { await api.post(`/leave-requests/${lr.id}/cancel`); toast.success("Richiesta annullata"); onRefresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const isDayRange = lr.start_date !== lr.end_date;
  return (
    <li className="py-2.5 flex items-start justify-between gap-3" data-testid={`leave-row-${lr.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-suite-neutral-800">{kindLabel}</span>
          <SuiteBadge tone={statusTone} size="sm">{statusLabel}</SuiteBadge>
        </div>
        <div className="text-xs text-suite-neutral-500 mt-0.5">
          {isDayRange ? `Dal ${lr.start_date} al ${lr.end_date}` : `Giorno ${lr.start_date}`}
          {" · "}<span className="font-mono">{fmtNum(lr.total_days)}</span> gg
          {" ("}<span className="font-mono">{fmtNum(lr.total_hours)}</span> h)
        </div>
        {lr.notes && <div className="text-xs text-suite-neutral-600 italic mt-0.5">&quot;{lr.notes}&quot;</div>}
        {lr.status === "rejected" && lr.rejection_reason && (
          <div className="text-xs text-suite-error mt-0.5">Motivazione: {lr.rejection_reason}</div>
        )}
      </div>
      {lr.status === "pending" && (
        <button type="button" onClick={cancelIt}
                className="text-suite-neutral-400 hover:text-suite-error transition-colors p-1"
                title="Annulla richiesta" data-testid={`leave-cancel-${lr.id}`}>
          <X size={14} />
        </button>
      )}
    </li>
  );
}

/** Dialog per proporre una nuova richiesta ferie/permesso. */
function LeaveRequestDialog({ open, onOpenChange, pools, onCreated }) {
  const [kind, setKind] = useState("FERIE");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const currentPool = pools[kind];
  const canSubmit = kind && startDate && endDate && startDate <= endDate && !busy;

  const reset = () => { setKind("FERIE"); setStartDate(""); setEndDate(""); setNotes(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/leave-requests", {
        kind, start_date: startDate, end_date: endDate, notes: notes || null,
      });
      toast.success("Richiesta inviata — verrà notificata all'amministratore");
      reset(); onOpenChange(false); onCreated?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) { if (!o) reset(); onOpenChange(o); } }}>
      <DialogContent formDialog className="max-w-md" data-testid="leave-request-dialog">
        <DialogHeader>
          <SuiteEyebrow tone="primary" icon={Palmtree}>Richiesta guidata</SuiteEyebrow>
          <DialogTitle className="mt-1" style={{ fontFamily: "Manrope" }}>Proponi ferie o permesso</DialogTitle>
          <DialogDescription>Il tuo responsabile riceverà una notifica e potrà approvare o respingere.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-suite-neutral-500">Causale</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="leave-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="FERIE">Ferie</SelectItem>
                <SelectItem value="PERMESSI">Permessi (ROL)</SelectItem>
                <SelectItem value="EX_FESTIVITA">Ex festività</SelectItem>
                <SelectItem value="PERMESSI_STUDIO">Permessi studio (150 ore)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-suite-neutral-500">Dal</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required data-testid="leave-start-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-suite-neutral-500">Al</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required data-testid="leave-end-date" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-suite-neutral-500">Motivazione (opzionale)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="es. visita medica, viaggio…" data-testid="leave-notes" />
          </div>
          {currentPool?.pool && (
            <div className="text-xs text-suite-neutral-600 border-l-2 border-suite-primary/40 pl-2 py-1 bg-suite-primary-light/30 rounded-r">
              Residuo attuale <strong>{kind === "FERIE" ? "ferie" : kind === "PERMESSI" ? "ROL" : "ex festività"}</strong>:{" "}
              <span className="font-mono font-semibold">{fmtVal(currentPool.pool.residual, currentPool.unit)}</span>
            </div>
          )}
          <DialogFooter>
            <SuiteButton variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>Annulla</SuiteButton>
            <SuiteButton type="submit" variant="primary" icon={Send} loading={busy} disabled={!canSubmit} data-testid="leave-submit-btn">
              Invia richiesta
            </SuiteButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
